import { useHistory } from "react-router-dom";

import { listFiltersService, ListFiltersService } from "./ListFiltersService";

type History = ReturnType<typeof useHistory>;

type Filters<T> = Partial<Record<keyof T, unknown>>;

interface PersistentListFilters {
    id?: string;
    search: string;
}

const PERSISTENT_LIST_FILTERS_MAX_LENGTH = 100;

export const persistentListFiltersService = <T extends Filters<T>>(defaultFilters: T): ListFiltersService<T> => {
    const urlFiltersService = listFiltersService<T>(defaultFilters);

    const getCurrentPathName = () => {
        const { hash } = window.location;
        const searchStartIndex = hash.indexOf("?");

        return searchStartIndex !== -1 ? hash.substring(0, searchStartIndex).replace("#", "") : hash.replace("#", "");
    };

    const getPathNameProperties = (pathName: string) => {
        const isUuid = (subPath: string) => {
            return !!subPath.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
        };

        const hasLengthAndIsNotUuid = (subPath: string) => subPath.length > 0 && !isUuid(subPath);

        const subPaths = pathName.split("/");
        const storageKey = `${subPaths.filter(hasLengthAndIsNotUuid).join("_").toUpperCase()}_FILTERS`;
        const uuid = subPaths.find(isUuid);

        return { storageKey, uuid };
    };

    const getPersistentListFiltersFromStorage = (storageKey: string) => {
        const persistentFiltersJSON = sessionStorage.getItem(storageKey);
        const persistentFilters = persistentFiltersJSON ? JSON.parse(persistentFiltersJSON) : [];

        return persistentFilters as PersistentListFilters[];
    };

    const savePersistentListFiltersToStorage = (storageKey: string, persistentFilters: PersistentListFilters[]) => {
        const persistentFiltersJSON = JSON.stringify(persistentFilters);
        sessionStorage.setItem(storageKey, persistentFiltersJSON);
    };

    const addToPersistentFilters = (newFilters: PersistentListFilters, persistentFilters: PersistentListFilters[]) => {
        const index = persistentFilters.findIndex(({ id }: PersistentListFilters) => id === newFilters.id);

        if (index >= 0 && index < persistentFilters.length) {
            persistentFilters.splice(index, 1);
        }

        persistentFilters.unshift(newFilters);

        while (persistentFilters.length > PERSISTENT_LIST_FILTERS_MAX_LENGTH) {
            persistentFilters.pop();
        }
    };

    const getSavedFilters = (search: string): T => {
        if (!search) {
            const pathName = getCurrentPathName();
            const { storageKey, uuid } = getPathNameProperties(pathName);
            const persistentFilters = getPersistentListFiltersFromStorage(storageKey);
            const persistentSearch = persistentFilters.find(({ id }: PersistentListFilters) => id === uuid)?.search;

            if (persistentSearch) {
                return urlFiltersService.getSavedFilters(persistentSearch);
            }
        }

        return urlFiltersService.getSavedFilters(search);
    };

    const saveFilters = (pathName: string, history: History, filters: T): void => {
        urlFiltersService.saveFilters(pathName, history, filters);

        const urlSavedSearch = history.location.search;

        const { storageKey, uuid } = getPathNameProperties(pathName);
        const persistentFilters = getPersistentListFiltersFromStorage(storageKey);
        const newFilters = { id: uuid, search: urlSavedSearch };

        addToPersistentFilters(newFilters, persistentFilters);
        savePersistentListFiltersToStorage(storageKey, persistentFilters);
    };

    return {
        getSavedFilters,
        saveFilters
    };
};
